import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, fileUrl, formatViews, timeAgoCa } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n';
import { translateCategory } from '../lib/categories';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AdSlot } from '../components/AdSlot';
import { AdSenseUnit } from '../components/AdSenseUnit';
import { Seo } from '../components/Seo';
import { VideoCard } from '../components/VideoCard';
import { Flag, Heart, Share2, Eye, MessageCircle, LogIn, Trash2, X, ChevronLeft, ChevronRight, PanelRight, Code2, UserPlus, UserMinus, Users, Download, ThumbsUp, ThumbsDown, CornerUpRight } from 'lucide-react';
import { toast } from 'sonner';

export default function WatchPage() {
    const { videoId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const { t, locale } = useI18n();

    const [data, setData] = useState(null);
    const [comments, setComments] = useState([]);
    const [replyTo, setReplyTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [showReportedComments, setShowReportedComments] = useState(false);
    const [comment, setComment] = useState('');
    const [adShowing, setAdShowing] = useState(false);
    const [adCountdown, setAdCountdown] = useState(5);
    const [preroll, setPreroll] = useState(null);
    const [htmlPreroll, setHtmlPreroll] = useState(null);
    const [promoPreroll, setPromoPreroll] = useState(null);
    const [overlayAd, setOverlayAd] = useState(null);
    const [overlayShown, setOverlayShown] = useState(true);
    const [viewLogged, setViewLogged] = useState(false);
    const [accessError, setAccessError] = useState(null);
    const [showEmbed, setShowEmbed] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [showFriendShare, setShowFriendShare] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [reportDetails, setReportDetails] = useState('');
    const reportReasons = ['Contingut inadequat', 'Violència o assetjament', 'Spam o engany', 'Infracció de drets d’autor', 'Contingut +18 no etiquetat', 'Altres'];
    const [friends, setFriends] = useState([]);
    const touchStartY = useRef(null);

    const moveToRelated = (direction) => {
        const related = data?.related || [];
        if (!related.length) return;
        const currentIndex = related.findIndex((item) => item.video_id === videoId);
        const nextIndex = direction === 'next'
            ? (currentIndex < 0 ? 0 : (currentIndex + 1) % related.length)
            : (currentIndex <= 0 ? related.length - 1 : currentIndex - 1);
        navigate(`/video/${related[nextIndex].video_id}`);
    };

    const handleTouchStart = (event) => { touchStartY.current = event.touches[0].clientY; };
    const handleTouchEnd = (event) => {
        if (touchStartY.current == null) return;
        const distance = event.changedTouches[0].clientY - touchStartY.current;
        touchStartY.current = null;
        if (Math.abs(distance) > 70) moveToRelated(distance < 0 ? 'next' : 'previous');
    };

    useEffect(() => {
        let cancelled = false;
        setPreroll(null);
        setHtmlPreroll(null);
        setPromoPreroll(null);
        setAdShowing(false);
        setViewLogged(false);
        (async () => {
            try {
                const r = await api.get(`/videos/${videoId}`);
                if (!cancelled) { setData(r.data); setAccessError(null); }
                const c = await api.get(`/videos/${videoId}/comments`, { params: { include_reported: showReportedComments } });
                if (!cancelled) setComments(c.data.items || []);
            } catch (error) {
                if (!cancelled) setAccessError(error.response?.data?.detail || 'No s’ha pogut carregar el vídeo');
                return;
            }
            // fetch ads
            try {
                const pr = await api.get('/ads', { params: { placement: 'preroll' } });
                const ov = await api.get('/ads', { params: { placement: 'overlay' } });
                if (!cancelled) {
                    const pickP = (pr.data.items || []);
                    const pickO = (ov.data.items || []);
                    if (pickP.length) {
                        const p = pickP[Math.floor(Math.random() * pickP.length)];
                        setPreroll(p);
                        setAdShowing(true);
                        setAdCountdown(5);
                        api.post(`/ads/${p.ad_id}/impression`).catch(() => {});
                    } else if (!r.data.video.is_premium) {
                        // No image preroll configured: fall back to a rotating HTML preroll for free videos.
                        let prerollShown = false;
                        try {
                            const htmlCfg = await api.get('/ads/platform-config', { params: { placement: 'preroll' } });
                            const codes = htmlCfg.data.html_codes?.length ? htmlCfg.data.html_codes : (htmlCfg.data.html_code ? [htmlCfg.data.html_code] : []);
                            if (!cancelled && htmlCfg.data.html_enabled && codes.length) {
                                setHtmlPreroll(codes[Math.floor(Math.random() * codes.length)]);
                                setAdShowing(true);
                                setAdCountdown(5);
                                prerollShown = true;
                            }
                        } catch {}
                        // Final fallback: user-paid promo preroll on free videos.
                        if (!prerollShown) {
                            try {
                                const promo = await api.get('/promo/ads/serve', { params: { placement: 'preroll' } });
                                if (!cancelled && promo.data?.ad_id) {
                                    setPromoPreroll(promo.data);
                                    setAdShowing(true);
                                    setAdCountdown(5);
                                    api.post(`/promo/ads/${promo.data.ad_id}/impression`).catch(() => {});
                                }
                            } catch {}
                        }
                    }
                    if (pickO.length) {
                        const o = pickO[Math.floor(Math.random() * pickO.length)];
                        setOverlayAd(o);
                        api.post(`/ads/${o.ad_id}/impression`).catch(() => {});
                    }
                }
            } catch {}
        })();
        return () => { cancelled = true; };
    }, [showReportedComments, videoId]);

    useEffect(() => {
        let cancelled = false;
        const refreshComments = async () => {
            try {
                const response = await api.get(`/videos/${videoId}/comments`, { params: { include_reported: showReportedComments } });
                if (!cancelled) setComments(response.data.items || []);
            } catch {}
        };
        const timer = window.setInterval(refreshComments, 3000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [showReportedComments, videoId]);

    // pre-roll countdown
    useEffect(() => {
        if (!adShowing) return;
        if (adCountdown <= 0) return;
        const t = setTimeout(() => setAdCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [adShowing, adCountdown]);

    const skipAd = () => { setAdShowing(false); };

    const onPlayStarted = () => {
        if (!viewLogged) {
            setViewLogged(true);
            if (data?.can_watch) api.post(`/videos/${videoId}/view`).catch(() => {});
        }
    };

    const openReport = () => {
        if (!user) { toast.error('Cal iniciar sessió per denunciar'); navigate('/entrar'); return; }
        setShowReport((value) => !value);
    };

    const submitReport = async () => {
        if (!reportReason) { toast.error('Tria un motiu de denúncia'); return; }
        try {
            const reason = reportDetails.trim() ? `${reportReason} · ${reportDetails.trim()}` : reportReason;
            const r = await api.post('/reports', { kind: 'video', target_id: videoId, reason });
            if (r.data && (r.data.action === 'video_banned' || r.data.action === 'video_deleted')) {
                toast.success('Vídeo bloquejat per acumulació de denúncies');
                navigate('/');
                return;
            }
            toast.success('Denúncia registrada');
            setShowReport(false);
            setReportReason('');
            setReportDetails('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error denunciant');
        }
    };

    const buyAccess = async () => {
        if (!user) { navigate('/entrar'); return; }
        try {
            const r = await api.post(`/videos/${videoId}/create-checkout-session`);
            if (r.data && r.data.url) {
                window.location.href = r.data.url;
                return;
            }
            if (r.data && r.data.mock) {
                toast.success('Compra simulada completada');
                const fresh = await api.get(`/videos/${videoId}`);
                setData(fresh.data);
                return;
            }
        } catch (e) {
            toast.error('No s\'ha pogut iniciar la compra');
        }
    };

    const toggleLike = async () => {
        if (!user) { toast.error(t('shorts.login_for_like')); navigate('/entrar'); return; }
        try {
            const r = await api.post(`/videos/${videoId}/like`);
            setData((d) => ({ ...d, liked: r.data.liked, video: { ...d.video, likes: d.video.likes + (r.data.liked ? 1 : -1) } }));
        } catch {}
    };

    const toggleFollow = async () => {
        if (!user) { toast.error('Inicia sessió per seguir creadors'); navigate('/entrar'); return; }
        if (followBusy) return;
        setFollowBusy(true);
        try {
            if (data.is_following) {
                await api.delete(`/users/${data.video.uploader_id}/follow`);
                setData((d) => ({ ...d, is_following: false }));
            } else {
                await api.post(`/users/${data.video.uploader_id}/follow`);
                setData((d) => ({ ...d, is_following: true }));
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut actualitzar el seguiment');
        } finally {
            setFollowBusy(false);
        }
    };

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) await navigator.share({ url, title: data?.video?.title });
            else { await navigator.clipboard.writeText(url); toast.success(t('watch.share_copied')); }
        } catch {}
    };

    const openFriendShare = async () => {
        if (!user) { toast.error(t('shorts.login_for_like')); navigate('/entrar'); return; }
        setShowFriendShare((value) => !value);
        try {
            const r = await api.get('/friends');
            setFriends(r.data.items || []);
        } catch {}
    };

    const shareToFriend = async (friendId) => {
        try {
            const form = new FormData();
            form.append('shared_video_id', videoId);
            await api.post(`/friends/${friendId}/messages`, form);
            toast.success('Vídeo compartit');
            setShowFriendShare(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut compartir');
        }
    };

    const embedCode = `<iframe src="${window.location.origin}/embed/${videoId}" title="${(data?.video?.title || 'Vídeo Albambam').replaceAll('"', '&quot;')}" width="560" height="315" style="border:0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;

    const copyEmbed = async () => {
        try {
            await navigator.clipboard.writeText(embedCode);
            toast.success('Codi embed copiat');
        } catch {
            toast.error('No s’ha pogut copiar el codi');
        }
    };

    const downloadVideo = async () => {
        try {
            const response = await api.get(`/videos/${videoId}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url; a.download = `albambam-${videoId}.mp4`;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            // Error responses also arrive as a Blob because of responseType: 'blob'.
            let detail = 'No s\u2019ha pogut baixar el v\u00eddeo';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    detail = JSON.parse(text)?.detail || detail;
                } catch {}
            } else {
                detail = error.response?.data?.detail || detail;
            }
            toast.error(detail);
        }
    };

    const submitComment = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        const r = await api.post(`/videos/${videoId}/comments`, { content: comment.trim() });
        setComments((cs) => [{ ...r.data, likes_count: 0, dislikes_count: 0, my_reaction: null, replies: [] }, ...cs]);
        setComment('');
        setData((d) => ({ ...d, video: { ...d.video, comments_count: (d.video.comments_count || 0) + 1 } }));
    };

    // Tree helpers for threaded comments (roots + replies).
    const applyToTree = (items, id, patch) => items.map((c) => {
        if (c.comment_id === id) return typeof patch === 'function' ? patch(c) : { ...c, ...patch };
        if ((c.replies || []).length) return { ...c, replies: applyToTree(c.replies, id, patch) };
        return c;
    });
    const removeFromTree = (items, id) => items.filter((c) => c.comment_id !== id).map((c) => ({ ...c, replies: removeFromTree(c.replies || [], id) }));

    const reactToComment = async (c, kind) => {
        if (!user) { toast.error(t('watch.login_to_comment')); navigate('/entrar'); return; }
        try {
            const r = await api.post(`/comments/${c.comment_id}/react`, { kind });
            const { likes, dislikes, my_reaction } = r.data;
            if (kind === 'dislike' && my_reaction === 'dislike') {
                // Disliked: hide the comment from this user's view.
                setComments((items) => removeFromTree(items, c.comment_id));
                return;
            }
            setComments((items) => applyToTree(items, c.comment_id, { likes_count: likes, dislikes_count: dislikes, my_reaction }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut votar el comentari');
        }
    };

    const submitReply = async (parentId, event) => {
        event.preventDefault();
        if (!replyText.trim()) return;
        try {
            const r = await api.post(`/comments/${parentId}/reply`, { content: replyText.trim() });
            setComments((items) => applyToTree(items, parentId, (c) => ({ ...c, replies: [...(c.replies || []), r.data] })));
            setData((d) => ({ ...d, video: { ...d.video, comments_count: (d.video.comments_count || 0) + 1 } }));
            setReplyTo(null);
            setReplyText('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut publicar la resposta');
        }
    };

    const deleteComment = async (c) => {
        if (!confirm(t('watch.delete_confirm'))) return;
        await api.delete(`/comments/${c.comment_id}`);
        const removedCount = 1 + ((c.replies || []).length);
        setComments((items) => removeFromTree(items, c.comment_id));
        setData((d) => ({ ...d, video: { ...d.video, comments_count: Math.max(0, (d.video.comments_count || 0) - removedCount) } }));
    };

    const reportComment = async (commentId) => {
        if (!user) { toast.error('Cal iniciar sessió per denunciar'); navigate('/entrar'); return; }
        try {
            await api.post('/reports', { kind: 'comment', target_id: commentId });
            setComments((items) => removeFromTree(items, commentId));
            toast.success('Comentari denunciat i ocultat');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s’ha pogut denunciar');
        }
    };

    const renderComment = (c, compact = false) => (
        <li key={c.comment_id} className="flex gap-3">
            <Avatar className={compact ? 'h-7 w-7 shrink-0' : 'h-8 w-8 shrink-0'}>
                <AvatarImage src={c.user_picture} />
                <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(c.user_name || 'U').slice(0,1).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
                <div className="text-sm"><span className="font-semibold">{c.user_name}</span> <span className="text-[color:var(--alb-text-muted)] text-xs">· {timeAgoCa(c.created_at)}</span></div>
                <div className="text-sm text-[color:var(--alb-text)]/90 whitespace-pre-wrap">{c.content}</div>
                {c.reported && <div className="mt-1 text-xs text-[color:var(--alb-sun)]">Comentari denunciat</div>}
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[color:var(--alb-text-muted)]">
                    <button onClick={() => reactToComment(c, 'like')} title="M'agrada" data-testid={`comment-like-${c.comment_id}`} className={`flex items-center gap-1 hover:text-[color:var(--alb-sun)] ${c.my_reaction === 'like' ? 'text-[color:var(--alb-sun)]' : ''}`}><ThumbsUp className="h-3.5 w-3.5" /> {c.likes_count || 0}</button>
                    <button onClick={() => reactToComment(c, 'dislike')} title="No m'agrada" data-testid={`comment-dislike-${c.comment_id}`} className={`flex items-center gap-1 hover:text-[color:var(--alb-pink)] ${c.my_reaction === 'dislike' ? 'text-[color:var(--alb-pink)]' : ''}`}><ThumbsDown className="h-3.5 w-3.5" /> {c.dislikes_count || 0}</button>
                    {user && <button onClick={() => { setReplyTo(replyTo === c.comment_id ? null : c.comment_id); setReplyText(''); }} className="flex items-center gap-1 hover:text-[color:var(--alb-aqua)]"><CornerUpRight className="h-3.5 w-3.5" /> Respondre</button>}
                    <button onClick={() => reportComment(c.comment_id)} title="Denunciar comentari" className="hover:text-[color:var(--alb-sun)]"><Flag className="h-3.5 w-3.5" /></button>
                    {(user && (user.user_id === c.user_id || user.user_id === v.uploader_id || user.role === 'admin')) && (
                        <button onClick={() => deleteComment(c)} title="Eliminar comentari" className="hover:text-[color:var(--alb-pink)]"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                </div>
                {replyTo === c.comment_id && (
                    <form onSubmit={(event) => submitReply(c.comment_id, event)} className="mt-2 flex gap-2">
                        <Textarea value={replyText} onChange={(event) => setReplyText(event.target.value)} placeholder="Escriu una resposta..." className="bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)] min-h-[60px] text-sm" />
                        <Button type="submit" className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Publicar</Button>
                    </form>
                )}
                {(c.replies || []).length > 0 && (
                    <ul className="mt-2 space-y-2 border-l border-[color:var(--alb-hairline)] pl-3">
                        {c.replies.map((reply) => renderComment(reply, compact))}
                    </ul>
                )}
            </div>
        </li>
    );

    if (accessError) {
        return <main className="mx-auto max-w-2xl px-3 sm:px-5 py-12 pb-24" data-testid="adult-access-gate">
            <Card className="p-6 text-center bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                <h1 className="font-display text-2xl font-semibold">Accés restringit</h1>
                <p className="mt-3 text-sm text-[color:var(--alb-text-muted)]">{accessError}</p>
                <p className="mt-2 text-sm text-[color:var(--alb-text-muted)]">Per al contingut +18 cal tenir 18 anys, una subscripció activa i l’opció “Mostrar +18” activada.</p>
                <Button className="mt-5 bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" onClick={() => navigate(user ? '/perfil' : '/entrar')}>{user ? 'Configurar accés' : 'Iniciar sessió'}</Button>
            </Card>
        </main>;
    }
    if (!data) {
        return <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-6 pb-24 md:pb-12">
            <div className="aspect-video w-full rounded-[16px] alb-pulse" />
        </main>;
    }
    const v = data.video;
    const src = fileUrl(v.video_url);
    // SEO: VideoObject structured data (Google rich results / video carousel)
    const seoJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: v.title,
        description: (v.description || `${v.title} a Albambam, la plataforma de vídeo en català.`).slice(0, 200),
        thumbnailUrl: v.thumbnail_url ? (v.thumbnail_url.startsWith('http') ? v.thumbnail_url : `https://albambam.com${v.thumbnail_url}`) : 'https://albambam.com/logo.jpg',
        uploadDate: v.created_at || undefined,
        contentUrl: `https://albambam.com/video/${v.video_id}`,
        ...(v.duration ? { duration: `PT${Math.floor(v.duration / 60)}M${Math.round(v.duration % 60)}S` } : {}),
        publisher: { '@type': 'Organization', name: 'Albambam', url: 'https://albambam.com/' },
    };

    return (
        <main onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-5 md:py-8 pb-24 md:pb-12" data-testid="watch-page">
            <Seo path={`/video/${v.video_id}`} title={v.title} description={(v.description || `Mira ${v.title} a Albambam, la plataforma de vídeo en català.`).slice(0, 160)} jsonLd={seoJsonLd} />
            <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 space-y-4">
                    {/* Player area */}
                    <div className="relative rounded-[16px] overflow-hidden border border-[color:var(--alb-hairline)] bg-black">
                        {adShowing && preroll ? (
                            <div data-testid="watch-preroll-ad" className="relative aspect-video w-full bg-black">
                                <img src={preroll.image_url} alt={preroll.title} className="absolute inset-0 h-full w-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
                                <div className="absolute left-4 top-4 z-10 inline-flex items-center rounded-full bg-[color:var(--alb-ad-badge)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--alb-ink)]">{t('ad.label')}</div>
                                <div className="absolute left-4 bottom-4 right-4 text-white max-w-[60%]">
                                    <div className="font-display text-xl md:text-3xl font-semibold">{preroll.title}</div>
                                    <a href={preroll.target_url} target="_blank" rel="noreferrer" onClick={() => api.post(`/ads/${preroll.ad_id}/click`).catch(() => {})} className="mt-2 inline-flex items-center text-[color:var(--alb-sun)] font-semibold">{t('ad.visit')} →</a>
                                </div>
                                <div className="absolute right-4 bottom-4">
                                    {adCountdown > 0 ? (
                                        <div className="px-3 py-2 rounded-full bg-black/60 text-white text-sm font-mono-tab">{t('ad.skip_in', { n: adCountdown })}</div>
                                    ) : (
                                        <button data-testid="watch-skip-ad-button" onClick={skipAd} className="px-3 py-2 rounded-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] text-sm font-semibold">{t('ad.skip')}</button>
                                    )}
                                </div>
                            </div>
                        ) : adShowing && htmlPreroll ? (
                            <div data-testid="watch-preroll-html-ad" className="relative aspect-video w-full bg-black overflow-hidden">
                                <div className="absolute left-4 top-4 z-10 inline-flex items-center rounded-full bg-[color:var(--alb-ad-badge)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--alb-ink)]">{t('ad.label')}</div>
                                <div className="absolute inset-0 grid place-items-center" dangerouslySetInnerHTML={{ __html: htmlPreroll }} />
                                <div className="absolute right-4 bottom-4">
                                    {adCountdown > 0 ? (
                                        <div className="px-3 py-2 rounded-full bg-black/60 text-white text-sm font-mono-tab">{t('ad.skip_in', { n: adCountdown })}</div>
                                    ) : (
                                        <button data-testid="watch-skip-html-ad-button" onClick={skipAd} className="px-3 py-2 rounded-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] text-sm font-semibold">{t('ad.skip')}</button>
                                    )}
                                </div>
                            </div>
                        ) : adShowing && promoPreroll ? (
                            <div data-testid="watch-preroll-promo-ad" className="relative aspect-video w-full bg-black">
                                {promoPreroll.thumbnail && <img src={promoPreroll.thumbnail} alt={promoPreroll.title} className="absolute inset-0 h-full w-full object-cover" />}
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
                                <div className="absolute left-4 top-4 z-10 inline-flex items-center rounded-full bg-[color:var(--alb-ad-badge)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--alb-ink)]">Promocionat</div>
                                <div className="absolute left-4 bottom-4 right-4 text-white max-w-[60%]">
                                    <div className="font-display text-xl md:text-3xl font-semibold">{promoPreroll.title}</div>
                                    {promoPreroll.kind === 'external' ? (
                                        <a href={promoPreroll.target_url} target="_blank" rel="noreferrer" onClick={() => api.post(`/promo/ads/${promoPreroll.ad_id}/click`).catch(() => {})} className="mt-2 inline-flex items-center text-[color:var(--alb-sun)] font-semibold">Visita el lloc &rarr;</a>
                                    ) : (
                                        <button type="button" onClick={() => { api.post(`/promo/ads/${promoPreroll.ad_id}/click`).catch(() => {}); navigate(promoPreroll.target_url); }} className="mt-2 inline-flex items-center text-[color:var(--alb-sun)] font-semibold">Mira-ho ara &rarr;</button>
                                    )}
                                </div>
                                <div className="absolute right-4 bottom-4">
                                    {adCountdown > 0 ? (
                                        <div className="px-3 py-2 rounded-full bg-black/60 text-white text-sm font-mono-tab">{t('ad.skip_in', { n: adCountdown })}</div>
                                    ) : (
                                        <button data-testid="watch-skip-promo-ad-button" onClick={skipAd} className="px-3 py-2 rounded-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] text-sm font-semibold">{t('ad.skip')}</button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="relative">
                                {v.is_premium && !data.can_watch ? (
                                    <div className="w-full aspect-video bg-black flex items-center justify-center text-white p-6">
                                        <div className="text-center">
                                            <div className="text-xl font-semibold mb-2">Aquest vídeo és premium</div>
                                            <div className="mb-4">Preu: {v.price?.toFixed(2)} {data?.video?.currency || 'EUR'}</div>
                                            <div className="flex items-center justify-center gap-2">
                                                <Button onClick={buyAccess} className="bg-[color:var(--alb-sun)]">Comprar accés</Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <video
                                        data-testid="watch-player"
                                        ref={videoRef}
                                        src={src}
                                        controls
                                        loop
                                        playsInline
                                        onPlay={onPlayStarted}
                                        className="w-full aspect-video bg-black"
                                    >
                                        {(data.video.subtitles || []).map((s, index) => (
                                            <track key={s.lang} src={s.url} kind="subtitles" srcLang={s.lang} label={s.name} default={index === 0} />
                                        ))}
                                    </video>
                                )}
                                {/* Overlay ad */}
                                {overlayAd && overlayShown && (
                                    <div data-testid="watch-overlay-ad" className="absolute left-3 right-3 bottom-16 md:bottom-20 rounded-xl overflow-hidden border border-[color:var(--alb-ad-border)] bg-[rgba(16,24,38,0.85)] backdrop-blur">
                                        <span className="absolute left-2 top-2 z-10 inline-flex items-center rounded-full bg-[color:var(--alb-ad-badge)] px-2 py-0.5 text-[10px] font-semibold text-[color:var(--alb-ink)]">{t('ad.label')}</span>
                                        <button data-testid="watch-overlay-close" onClick={() => setOverlayShown(false)} className="absolute right-2 top-2 z-10 h-7 w-7 grid place-items-center rounded-full bg-black/60 text-white"><X className="h-4 w-4" /></button>
                                        <a href={overlayAd.target_url} target="_blank" rel="noreferrer" onClick={() => api.post(`/ads/${overlayAd.ad_id}/click`).catch(() => {})} className="flex items-center gap-3 p-2">
                                            <img src={overlayAd.image_url} alt={overlayAd.title} className="h-12 w-20 object-cover rounded-md" />
                                            <div className="min-w-0">
                                                <div className="font-semibold text-white truncate">{overlayAd.title}</div>
                                                <div className="text-xs text-[color:var(--alb-text-muted)] truncate">{overlayAd.advertiser || 'Patrocinat'}</div>
                                            </div>
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Title row */}
                    <div className="flex flex-col gap-3">
                        <h1 data-testid="watch-title" className="font-display text-xl md:text-2xl font-semibold">{v.title}</h1>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <Link to={`/usuari/${v.uploader_id}`} className="flex items-center gap-3">
                                <Avatar className="h-10 w-10 ring-1 ring-[color:var(--alb-hairline)]">
                                    <AvatarImage src={v.uploader_picture} />
                                    <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(v.uploader_name || 'U').slice(0,1).toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <div className="font-semibold">{v.uploader_name}</div>
                                    <div className="text-xs text-[color:var(--alb-text-muted)] flex items-center gap-2"><Eye className="h-3 w-3" /> {formatViews(v.views)} visualitzacions · {timeAgoCa(v.created_at)}</div>
                                </div>
                            </Link>
                            {user && user.user_id !== v.uploader_id && (
                                <Button
                                    data-testid="watch-follow-button"
                                    onClick={toggleFollow}
                                    disabled={followBusy}
                                    variant={data.is_following ? 'outline' : 'default'}
                                    className={data.is_following ? '' : 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]'}
                                >
                                    {data.is_following ? <UserMinus className="h-4 w-4 mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                                    {data.is_following ? 'Seguint' : 'Seguir'}
                                </Button>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-text)]">{translateCategory(v.category, locale)}</Badge>
                                <Button data-testid="watch-like-button" onClick={toggleLike} variant="secondary" className={`bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)] ${data.liked ? 'text-[color:var(--alb-pink)]' : ''}`}>
                                    <Heart className={`h-4 w-4 mr-1 ${data.liked ? 'fill-[color:var(--alb-pink)]' : ''}`} /> {formatViews(v.likes)}
                                </Button>
                                <Button data-testid="watch-share-button" onClick={share} variant="secondary" className="bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)]"><Share2 className="h-4 w-4 mr-1" /> {t('watch.share')}</Button>
                                <Button data-testid="watch-friend-share-button" onClick={openFriendShare} variant="secondary" className="bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)]"><Users className="h-4 w-4 mr-1" /> {t('friends.share_video')}</Button>
                                <Button data-testid="watch-embed-button" onClick={() => setShowEmbed((value) => !value)} variant="secondary" className="bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)]"><Code2 className="h-4 w-4 mr-1" /> Incrusta</Button>
                                {data.can_watch && <Button data-testid="watch-download-button" onClick={downloadVideo} variant="secondary" className="bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)]"><Download className="h-4 w-4 mr-1" /> Baixa</Button>}
                                <Button onClick={() => setShowComments((value) => !value)} variant="secondary" aria-label="Mostrar comentaris" title="Mostrar comentaris" className={`bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)] ${showComments ? 'text-[color:var(--alb-aqua)]' : ''}`}><PanelRight className="h-4 w-4" /></Button>
                                <Button data-testid="watch-report-button" onClick={openReport} variant="secondary" className="bg-[color:var(--alb-surface-2)] hover:bg-[color:var(--alb-elevated)] text-red-600">Denunciar</Button>
                            </div>
                        </div>
                        {showReport && (
                            <Card className="p-4 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="watch-report-panel">
                                <h3 className="font-semibold mb-2">Motiu de la denúncia</h3>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {reportReasons.map((r) => (
                                        <label key={r} className="flex items-center gap-2 text-sm">
                                            <input type="radio" name="report-reason" checked={reportReason === r} onChange={() => setReportReason(r)} />
                                            {r}
                                        </label>
                                    ))}
                                </div>
                                <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder="Detalls opcionals..." rows={2} className="mt-3 bg-[color:var(--alb-ink-2)]" />
                                <div className="mt-3 flex gap-2">
                                    <Button data-testid="watch-report-submit" onClick={submitReport} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Enviar denúncia</Button>
                                    <Button variant="outline" onClick={() => setShowReport(false)}>Cancel·lar</Button>
                                </div>
                            </Card>
                        )}
                        {v.description && (
                            <Card className="p-4 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <p className="whitespace-pre-wrap text-sm text-[color:var(--alb-text)]/90">{v.description}</p>
                            </Card>
                        )}
                        {showEmbed && <Card className="p-4 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]"><div className="flex items-center justify-between gap-3"><h2 className="font-semibold">Incrusta aquest vídeo</h2><Button size="sm" onClick={copyEmbed} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">Copiar codi</Button></div><p className="mt-2 text-xs text-[color:var(--alb-text-muted)]">Enganxa aquest codi en una altra pàgina web.</p><textarea readOnly value={embedCode} className="mt-3 min-h-20 w-full rounded border border-[color:var(--alb-hairline)] bg-[color:var(--alb-ink-2)] p-3 font-mono text-xs" aria-label="Codi per incrustar el vídeo" /> </Card>}
                        {showFriendShare && (
                            <Card className="p-4 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]" data-testid="friend-share-panel">
                                <h2 className="font-semibold mb-2">{t('friends.share_video')}</h2>
                                {friends.length === 0 ? (
                                    <p className="text-sm text-[color:var(--alb-text-muted)]">{t('friends.empty_title')}</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {friends.map((f) => (
                                            <li key={f.user.user_id} className="flex items-center justify-between gap-3">
                                                <span className="flex items-center gap-2 text-sm"><Avatar className="h-7 w-7"><AvatarImage src={f.user.picture} /><AvatarFallback>{(f.user.name || 'U').slice(0,1)}</AvatarFallback></Avatar>{f.user.name}</span>
                                                <Button size="sm" onClick={() => shareToFriend(f.user.user_id)} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">{t('friends.send')}</Button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>
                        )}
                        {/* Adult warnings removed */}
                    </div>

                    {/* Banner ad below player; postroll promo for free videos */}
                    <AdSlot placement="banner" />
                    {!v.is_premium && <AdSlot placement="postroll" />}
                    <AdSenseUnit placement="watch" />

                    <div className="flex items-center justify-between gap-3 border-y border-[color:var(--alb-hairline)] py-3">
                        <span className="text-sm text-[color:var(--alb-text-muted)]">Llisca amunt o avall per canviar de vídeo</span>
                        <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => moveToRelated('previous')} aria-label="Vídeo anterior" title="Vídeo anterior"><ChevronLeft className="h-4 w-4" /></Button><Button size="sm" variant="outline" onClick={() => moveToRelated('next')} aria-label="Vídeo següent" title="Vídeo següent"><ChevronRight className="h-4 w-4" /></Button></div>
                    </div>

                    {/* Comments */}
                    <section data-testid="watch-comments-section" className={`${showComments ? '' : 'hidden'} lg:hidden space-y-3`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4" /> {v.comments_count} {t('watch.comments')}</h3>
                            <label className="flex items-center gap-2 text-xs text-[color:var(--alb-text-muted)]">
                                <input type="checkbox" checked={showReportedComments} onChange={(event) => setShowReportedComments(event.target.checked)} />
                                Mostrar comentaris denunciats
                            </label>
                        </div>
                        {v.comments_enabled === false ? (
                            <Card className="p-5 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)] text-sm text-[color:var(--alb-text-muted)]">
                                El creador ha desactivat els comentaris d’aquest vídeo.
                            </Card>
                        ) : user ? (
                            <form onSubmit={submitComment} className="flex gap-2">
                                <Avatar className="h-9 w-9">
                                    <AvatarImage src={user.picture} />
                                    <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(user.name || 'U').slice(0,1).toUpperCase()}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 space-y-2">
                                    <Textarea data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('watch.write_comment')} className="bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)] min-h-[80px]" />
                                    <div className="flex justify-end">
                                        <Button data-testid="comment-submit-button" type="submit" className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">{t('watch.publish')}</Button>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            <Card data-testid="comments-auth-gate" className="p-5 flex flex-col sm:flex-row items-center gap-3 justify-between bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                                <p className="text-sm text-[color:var(--alb-text-muted)]">{t('watch.login_to_comment')}</p>
                                <Button data-testid="comments-login-button" onClick={() => navigate('/entrar')} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]"><LogIn className="h-4 w-4 mr-1" /> {t('watch.login_btn')}</Button>
                            </Card>
                        )}
                        <ul className="space-y-3" data-testid="comments-list">
                            {comments.map((c) => renderComment(c))}
                            {comments.length === 0 && <li className="text-sm text-[color:var(--alb-text-muted)]">{t('watch.no_comments')}</li>}
                        </ul>
                    </section>
                </div>

                {/* Related sidebar */}
                <aside className="lg:col-span-4 space-y-3">
                    {showComments && <section data-testid="watch-comments-sidebar" className="space-y-3 rounded-[16px] border border-[color:var(--alb-hairline)] bg-[color:var(--alb-surface)] p-4 lg:sticky lg:top-4">
                        <div className="flex items-center justify-between"><h3 className="font-display text-lg font-semibold flex items-center gap-2"><MessageCircle className="h-4 w-4" /> {v.comments_count} {t('watch.comments')}</h3><Button size="sm" variant="ghost" onClick={() => setShowComments(false)} aria-label="Tancar comentaris"><X className="h-4 w-4" /></Button></div>
                        {v.comments_enabled !== false && user && <form onSubmit={submitComment} className="space-y-2"><Textarea data-testid="sidebar-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('watch.write_comment')} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" /><Button type="submit" className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]">{t('watch.publish')}</Button></form>}
                        {v.comments_enabled === false && <p className="text-sm text-[color:var(--alb-text-muted)]">El creador ha desactivat els comentaris.</p>}
                        <ul className="space-y-3 max-h-[60vh] overflow-y-auto">{comments.map((c) => renderComment(c, true))}{comments.length === 0 && <li className="text-sm text-[color:var(--alb-text-muted)]">{t('watch.no_comments')}</li>}</ul>
                    </section>}
                    <h3 className="font-display text-lg font-semibold">{t('watch.related')}</h3>
                    <AdSlot placement="banner" />
                    <div className="space-y-3" data-testid="watch-related">
                        {(data.related || []).map((rv) => (
                            <VideoCard key={rv.video_id} video={rv} compact />
                        ))}
                    </div>
                </aside>
            </div>
        </main>
    );
}
